/**
 * Client state only.
 *
 * Everything the server owns lives in React Query. What is left is small:
 * which engagement is selected, and the toasts currently on screen. Portal
 * slots stay in `provider/pageTitle.tsx` — those are live DOM nodes, which do
 * not belong in a serialisable store.
 *
 * `workspace` survives a reload via redux-persist (see `persistConfig.ts`);
 * `toast` does not.
 */
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
	FLUSH,
	PAUSE,
	PERSIST,
	PURGE,
	REGISTER,
	REHYDRATE,
	persistReducer,
	persistStore,
} from 'redux-persist';
import { useDispatch, useSelector } from 'react-redux';
import { persistConfig } from './persistConfig';
import runWatch from './runWatchSlice';
import toast from './toastSlice';
import ui from './uiSlice';
import workspace from './workspaceSlice';

const rootReducer = combineReducers({ runWatch, toast, ui, workspace });
const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [
					FLUSH,
					REHYDRATE,
					PAUSE,
					PERSIST,
					PURGE,
					REGISTER,
				],
			},
		}),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
